import React, { useEffect, useMemo, useState } from 'react';
import { downloadRecetasImportErrorsReport } from '../../utils/recetasExcelImport';
import { RECETAS_IMPORT_MODE } from '../../utils/recetasExcelPepUsoUpdate';

const th = 'px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider';
const td = 'px-2 py-2 text-sm text-gray-800 border-t border-gray-100 align-top max-w-[10rem] break-words';

function statusBadge(status) {
  if (status === 'valid') {
    return <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Válida</span>;
  }
  if (status === 'skipped') {
    return <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Omitida</span>;
  }
  if (status === 'empty') {
    return <span className="text-xs text-gray-500">Vacía</span>;
  }
  return <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">Error</span>;
}

const RecetasImportPreviewModal = ({ isOpen, onClose, preview, includeOutputConfig = false, onConfirm, confirming }) => {
  const [rowFilter, setRowFilter] = useState('all');
  const [exportingReport, setExportingReport] = useState(false);

  const isPepUsoBulkUpdate = preview?.mode === RECETAS_IMPORT_MODE.PEP_USO_BULK_UPDATE;

  const { previewRows, recipeCount, validIngredientRows, emptyCount, invalidCount, updateCount, skippedCount } =
    preview?.success ? preview : {};

  useEffect(() => {
    if (isOpen) {
      setRowFilter('all');
    }
  }, [isOpen]);

  const displayedRows = useMemo(() => {
    if (!previewRows) return [];
    if (rowFilter === 'errors') {
      return previewRows.filter((r) => r.status === 'invalid');
    }
    return previewRows;
  }, [previewRows, rowFilter]);

  if (!isOpen || !preview?.success) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !confirming) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recetas-import-preview-title"
      >
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h3 id="recetas-import-preview-title" className="text-lg font-semibold text-gray-900">
            {isPepUsoBulkUpdate ? 'Vista previa — actualizar uso en guía PT' : 'Vista previa — importar recetas'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isPepUsoBulkUpdate ? (
              <>
                Actualización masiva desde el Excel exportado. Se modifica solo{' '}
                <strong>«Uso en guía PT»</strong> en recetas <strong>Producto en proceso</strong>, identificadas por{' '}
                <strong>código de receta</strong>.
              </>
            ) : (
              <>
                Cada <strong>fila</strong> es un ingrediente; el mismo nombre y familia agrupan una receta. La unidad del
                ingrediente se toma del producto insumo.
                {includeOutputConfig ? (
                  <>
                    {' '}
                    En <strong>Producción</strong> también se importan tipo de producto de salida, cantidad, unidad y
                    uso en guía PT (se heredan en el bloque como familia y nombre).
                  </>
                ) : null}
              </>
            )}
          </p>
          <p className="text-sm mt-2">
            {isPepUsoBulkUpdate ? (
              <>
                <span className="text-green-700 font-medium">{updateCount ?? recipeCount ?? 0} receta(s) a actualizar</span>
                {(skippedCount ?? 0) > 0 ? (
                  <>
                    {' · '}
                    <span className="text-gray-500">{skippedCount} fila(s) omitida(s)</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <span className="text-green-700 font-medium">{recipeCount} receta(s)</span>
                <>
                  {' · '}
                  <span className="text-gray-700">{validIngredientRows} fila(s) de ingrediente válida(s)</span>
                </>
                {emptyCount > 0 ? (
                  <>
                    {' · '}
                    <span className="text-gray-500">{emptyCount} vacía(s) (omitidas)</span>
                  </>
                ) : null}
              </>
            )}
            {invalidCount > 0 ? (
              <>
                {' · '}
                <span className="text-red-700 font-medium">{invalidCount} con error</span>
              </>
            ) : null}
          </p>
          {invalidCount > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="text-gray-600 font-medium">Ver filas:</span>
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setRowFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    rowFilter === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Todas ({previewRows.length})
                </button>
                <button
                  type="button"
                  onClick={() => setRowFilter('errors')}
                  className={`px-3 py-1.5 text-sm font-medium border-l border-gray-200 transition-colors ${
                    rowFilter === 'errors'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Solo con error ({invalidCount})
                </button>
              </div>
              <button
                type="button"
                disabled={confirming || exportingReport}
                onClick={async () => {
                  setExportingReport(true);
                  try {
                    await downloadRecetasImportErrorsReport(previewRows, { includeOutputConfig });
                  } finally {
                    setExportingReport(false);
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                {exportingReport ? 'Generando…' : 'Descargar reporte de errores (Excel)'}
              </button>
            </div>
          ) : null}
        </div>

        <div className="overflow-auto flex-1 p-4">
          {rowFilter === 'errors' && invalidCount > 0 ? (
            <p className="text-xs text-gray-500 mb-2">
              Mostrando {displayedRows.length} fila(s) con error de {previewRows.length} en el archivo.
            </p>
          ) : null}
          <table className="min-w-full border border-gray-200 rounded-md overflow-hidden text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className={th}>Fila</th>
                {isPepUsoBulkUpdate ? (
                  <>
                    <th className={th}>Código</th>
                    <th className={th}>Nombre</th>
                    <th className={th}>Tipo salida</th>
                    <th className={th}>Uso actual</th>
                    <th className={th}>Uso nuevo</th>
                  </>
                ) : (
                  <>
                    <th className={th}>Familia</th>
                    <th className={th}>Nombre receta</th>
                    {includeOutputConfig ? (
                      <>
                        <th className={th}>Tipo salida</th>
                        <th className={th}>Cant. salida</th>
                        <th className={th}>Unid. salida</th>
                        <th className={th}>Uso guía PT</th>
                      </>
                    ) : null}
                    <th className={th}>Insumo</th>
                    <th className={th}>Cantidad</th>
                    <th className={th}>Activa</th>
                  </>
                )}
                <th className={th}>Estado</th>
                <th className={th}>Detalle</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {displayedRows.map((row) => (
                <tr
                  key={row.excelRow}
                  className={row.status === 'invalid' ? 'bg-red-50/50' : row.status === 'valid' ? 'bg-green-50/30' : ''}
                >
                  <td className={td}>{row.excelRow}</td>
                  {isPepUsoBulkUpdate ? (
                    <>
                      <td className={td}>{row.cols?.codigo ?? '—'}</td>
                      <td className={td}>{row.cols?.nombre ?? '—'}</td>
                      <td className={td}>{row.cols?.tipoSalida ?? '—'}</td>
                      <td className={td}>{row.cols?.usoActual ?? '—'}</td>
                      <td className={td}>{row.cols?.usoNuevo ?? '—'}</td>
                    </>
                  ) : (
                    <>
                      <td className={td}>{row.cols?.familia ?? '—'}</td>
                      <td className={td}>{row.cols?.nombre ?? '—'}</td>
                      {includeOutputConfig ? (
                        <>
                          <td className={td}>{row.cols?.tipoSalida ?? '—'}</td>
                          <td className={td}>{row.cols?.cantidadSalida ?? '—'}</td>
                          <td className={td}>{row.cols?.unidadSalida ?? '—'}</td>
                          <td className={td}>{row.cols?.usoEnGuiaPt ?? '—'}</td>
                        </>
                      ) : null}
                      <td className={td}>{row.cols?.insumo ?? '—'}</td>
                      <td className={td}>{row.cols?.cantidad ?? '—'}</td>
                      <td className={td}>{row.cols?.activa ?? '—'}</td>
                    </>
                  )}
                  <td className={`${td} whitespace-nowrap`}>{statusBadge(row.status)}</td>
                  <td className={`${td} max-w-xs`}>{row.issue || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {previewRows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay filas bajo los encabezados.</p>
          ) : rowFilter === 'errors' && displayedRows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay filas con error (o el filtro no aplica).</p>
          ) : null}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={
              confirming ||
              (isPepUsoBulkUpdate ? (updateCount ?? 0) === 0 : recipeCount === 0)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirming
              ? isPepUsoBulkUpdate
                ? 'Actualizando…'
                : 'Importando…'
              : isPepUsoBulkUpdate
                ? `Confirmar actualización (${updateCount ?? recipeCount ?? 0} receta(s))`
                : `Confirmar importación (${recipeCount} receta(s))`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecetasImportPreviewModal;
